import type { Anchor, BoardConnection, BoardElement, End } from '../../api/boards'
import type { Point, Rect } from './viewport'
import { middleOf } from './viewport'

/**
 * Where a line runs, worked out from the two things it hangs off.
 *
 * **Pure functions and no React or DOM**, like `viewport.ts` and for the same reason: which side of
 * a card a line leaves from, where an elbow turns, and what a waypoint does to it is arithmetic, and
 * arithmetic is worth checking without a browser. `Connections.tsx` turns what comes out of here
 * into an `<svg>` and nothing more.
 *
 * The one rule worth stating twice: **a line is drawing on its board**. It says "these two belong
 * together in this arrangement" and nothing about the contents behind them, which is a decision the
 * linking issue makes for every kind of content at once.
 */

/**
 * The places on an element a line can be fastened to, in the order they are offered and drawn.
 *
 * The four sides and the four corners. `auto` is not among them because it is not a place — it is
 * the absence of a choice, and it is what an end gets when it is let go of over the middle of
 * something rather than over one of these.
 */
export const ANCHORS: readonly Exclude<Anchor, 'auto'>[] = [
  'top-left',
  'top',
  'top-right',
  'right',
  'bottom-right',
  'bottom',
  'bottom-left',
  'left',
]

/**
 * The point on a rectangle a line is fastened at.
 *
 * A named place is exactly that place, and stays there however the cards move. **`auto` is the
 * shortest way**: the line runs at the middle of the element and stops where it meets the edge, so
 * the arrow head lands somewhere on the frame rather than at one of eight fixed spots. That is what
 * somebody means when they drop a line on a card without aiming at anything in particular.
 */
export function anchorPoint(rect: Rect, anchor: Anchor, towards: Point): Point {
  if (anchor === 'auto') {
    return whereItMeetsTheEdge(rect, towards)
  }
  const middle = middleOf(rect)
  const right = rect.x + rect.width
  const bottom = rect.y + rect.height
  switch (anchor) {
    case 'top':
      return { x: middle.x, y: rect.y }
    case 'bottom':
      return { x: middle.x, y: bottom }
    case 'left':
      return { x: rect.x, y: middle.y }
    case 'right':
      return { x: right, y: middle.y }
    case 'top-left':
      return { x: rect.x, y: rect.y }
    case 'top-right':
      return { x: right, y: rect.y }
    case 'bottom-right':
      return { x: right, y: bottom }
    default:
      return { x: rect.x, y: bottom }
  }
}

/**
 * Where the straight line from the middle of [rect] towards [towards] crosses its edge.
 *
 * Scaled rather than reasoned about side by side: the direction is stretched until it reaches either
 * the horizontal or the vertical edge, whichever comes first, and that answers "which side" and
 * "where on it" in one go. A point *inside* the rectangle has no crossing, and the middle is then
 * the only honest answer.
 */
function whereItMeetsTheEdge(rect: Rect, towards: Point): Point {
  const middle = middleOf(rect)
  const dx = towards.x - middle.x
  const dy = towards.y - middle.y
  if (dx === 0 && dy === 0) {
    return middle
  }
  const horizontal = dx === 0 ? Infinity : rect.width / 2 / Math.abs(dx)
  const vertical = dy === 0 ? Infinity : rect.height / 2 / Math.abs(dy)
  const reach = Math.min(horizontal, vertical)
  return { x: middle.x + dx * reach, y: middle.y + dy * reach }
}

/** Whether [point] lies inside [rect] — how a released end finds what it was dropped on. */
export function within(rect: Rect, point: Point): boolean {
  return (
    point.x >= rect.x &&
    point.y >= rect.y &&
    point.x <= rect.x + rect.width &&
    point.y <= rect.y + rect.height
  )
}

/**
 * The topmost element under [point], or `null` when there is none.
 *
 * Topmost, because that is what somebody sees and therefore what they aimed at: the elements arrive
 * bottom to top, so the last one holding the point is the one on top of the pile.
 */
export function elementAt(
  point: Point,
  elements: readonly BoardElement[],
): BoardElement | undefined {
  return elements.filter((one) => within(rectOf(one), point)).at(-1)
}

/**
 * Which anchor an end let go of at [point] should take.
 *
 * **The whole border of a card snaps, not a small circle around each dot.** It used to be the circle,
 * and that was the wrong shape of rule: while an end is being dragged the pointer is captured, so
 * what the dots are worth as targets does not come into it at all — the only thing that decides is
 * this arithmetic. Twenty pixels around eight points on a card the size of a postcard leaves most of
 * it falling back to `auto`, and aiming at a corner felt like threading a needle.
 *
 * So: the middle of a card means "join these two and work out where yourself", and **anything outside
 * that middle takes the nearest of the eight**. Near a dot is still near a dot, wherever it is.
 */
export function anchorAt(rect: Rect, point: Point, near: number): Anchor {
  let closest: Exclude<Anchor, 'auto'> | null = null
  let distance = Infinity
  for (const anchor of ANCHORS) {
    const at = anchorPoint(rect, anchor, point)
    const away = Math.hypot(at.x - point.x, at.y - point.y)
    if (away < distance) {
      distance = away
      closest = anchor
    }
  }
  if (closest === null) {
    return 'auto'
  }
  return distance <= near || !inTheMiddleOf(rect, point) ? closest : 'auto'
}

/**
 * Whether [point] is well inside [rect] rather than out towards its edge.
 *
 * The middle half of it, in each direction — big enough to drop something on without aiming, small
 * enough that the band around it is a comfortable target for every one of the eight anchors.
 */
function inTheMiddleOf(rect: Rect, point: Point): boolean {
  const inset = { x: rect.width * MIDDLE, y: rect.height * MIDDLE }
  return (
    point.x > rect.x + inset.x &&
    point.x < rect.x + rect.width - inset.x &&
    point.y > rect.y + inset.y &&
    point.y < rect.y + rect.height - inset.y
  )
}

/** How much of a card, on each side, counts as "the middle" rather than as aiming at an anchor. */
const MIDDLE = 0.25

/**
 * How close to an anchor counts as near it even in the middle of a card, in **screen** pixels.
 *
 * It matters where the eight are close together — a small card, or one zoomed well out — and the
 * band around the middle would otherwise be the only way to reach them.
 */
export const ANCHOR_WITHIN = 24

/** The rectangle an element occupies, in board coordinates. */
export function rectOf(element: BoardElement): Rect {
  return { x: element.x, y: element.y, width: element.width, height: element.height }
}

/**
 * Where one end of a line actually is.
 *
 * A free end is its own point. A docked end is a point on a rectangle, and needs to know where the
 * *other* end is before `auto` can choose a side — which is why both ends are worked out together in
 * {@link endsOf} rather than one at a time.
 */
function pointOf(
  end: End,
  elements: ReadonlyMap<string, BoardElement>,
  towards: Point,
): Point | null {
  if (end.element === null || end.element === undefined) {
    return { x: end.x ?? 0, y: end.y ?? 0 }
  }
  const element = elements.get(end.element)
  if (element === undefined) {
    // The element has gone — from another tab, or a moment ago on this one. A line to nowhere is not
    // drawn at all; it goes with its element the next time the board is read.
    return null
  }
  return anchorPoint(rectOf(element), end.anchor ?? 'auto', towards)
}

/** Which edge of a rectangle an end sits on. `null` for an end that hangs free on the surface. */
export type Side = 'top' | 'right' | 'bottom' | 'left'

/**
 * Both ends of a line — where they are, **and which way they face**.
 *
 * The direction is not decoration. A line leaving the bottom of a card has to set off *downwards*;
 * one arriving at the right of another has to come in *from the right*. Without that a right-angled
 * connector leaves a bottom anchor sideways and reads as nonsense, which is exactly what it did.
 *
 * The provisional point either end aims at is the *middle* of the other's rectangle, because `auto`
 * has to choose where to sit before there is a point to aim at.
 */
export interface Ends {
  readonly from: Point
  readonly to: Point
  readonly fromSide: Side | null
  readonly toSide: Side | null
}

export function endsOf(
  line: Pick<BoardConnection, 'from' | 'to'>,
  elements: ReadonlyMap<string, BoardElement>,
): Ends | null {
  const towardsTo = provisional(line.to, elements)
  const towardsFrom = provisional(line.from, elements)
  const from = pointOf(line.from, elements, towardsTo)
  const to = pointOf(line.to, elements, towardsFrom)
  if (from === null || to === null) {
    return null
  }
  return {
    from,
    to,
    fromSide: sideOf(line.from, elements, from, towardsTo),
    toSide: sideOf(line.to, elements, to, towardsFrom),
  }
}

/**
 * Which edge an end sits on, or `null` when it hangs free.
 *
 * A point on a **corner** sits on two edges at once, and the one it leaves along is the one that
 * gets it closer to the other end — otherwise a line out of a top-left corner would always set off
 * upwards, however far to the left the thing it joins happens to be.
 */
function sideOf(
  end: End,
  elements: ReadonlyMap<string, BoardElement>,
  at: Point,
  towards: Point,
): Side | null {
  if (end.element === null || end.element === undefined) {
    return null
  }
  const element = elements.get(end.element)
  if (element === undefined) {
    return null
  }
  const rect = rectOf(element)
  const onLeft = Math.abs(at.x - rect.x) < ON_THE_EDGE
  const onRight = Math.abs(at.x - (rect.x + rect.width)) < ON_THE_EDGE
  const onTop = Math.abs(at.y - rect.y) < ON_THE_EDGE
  const onBottom = Math.abs(at.y - (rect.y + rect.height)) < ON_THE_EDGE

  if ((onLeft || onRight) && (onTop || onBottom)) {
    const sideways = Math.abs(towards.x - at.x) >= Math.abs(towards.y - at.y)
    if (sideways) {
      return onLeft ? 'left' : 'right'
    }
    return onTop ? 'top' : 'bottom'
  }
  if (onLeft) {
    return 'left'
  }
  if (onRight) {
    return 'right'
  }
  if (onTop) {
    return 'top'
  }
  return onBottom ? 'bottom' : null
}

/** Half a pixel at any zoom this surface allows — enough to say "this point is on that edge". */
const ON_THE_EDGE = 0.5

function provisional(end: End, elements: ReadonlyMap<string, BoardElement>): Point {
  if (end.element === null || end.element === undefined) {
    return { x: end.x ?? 0, y: end.y ?? 0 }
  }
  const element = elements.get(end.element)
  return element === undefined ? { x: 0, y: 0 } : middleOf(rectOf(element))
}

/**
 * The corners a line passes through, from one end to the other.
 *
 * A straight line and a curve have none of their own — they run through whatever waypoints there
 * are. An **elbow** is the one that has to think, and what it thinks about is the *directions* of
 * its two ends:
 *
 * - a line leaving the bottom of a card sets off **downwards**, and one leaving the right sets off
 *   **to the right**. It steps [STUB] clear of the edge before it turns at all;
 * - it arrives at the other end the same way round, coming in along that anchor's own direction, so
 *   the arrow head meets the edge square rather than sliding along it.
 *
 * Without that it left a bottom anchor sideways and doubled back over the card it came from — the
 * shape somebody reasonably called nonsense. What lies between the two stubs is then one turn, or
 * two where both ends face the same way, chosen so that neither stub is reversed.
 *
 * **Waypoints take over where there are any.** Somebody who has dragged corners in has said what the
 * line should do; the stubs are still stepped off, and the middle passes through what they placed.
 */
export function cornersOf(
  from: Point,
  to: Point,
  routing: BoardConnection['routing'],
  waypoints: readonly Point[],
  fromSide: Side | null = null,
  toSide: Side | null = null,
): Point[] {
  // A curve passes through the same points a straight line does — what differs is how it gets from
  // one to the next, which is `pathOf`'s business and not this one's.
  if (routing !== 'elbow') {
    return [from, ...waypoints, to]
  }

  const out = normalOf(fromSide)
  const back = normalOf(toSide)
  const a = out === null ? from : stepped(from, out)
  const b = back === null ? to : stepped(to, back)

  const middle =
    waypoints.length > 0 ? chained([a, ...waypoints, b]) : [a, ...turns(a, b, out, back), b]

  return collapsed([from, ...middle, to])
}

/** How far a line steps clear of an edge before it is allowed to turn at all. */
export const STUB = 24

interface Normal {
  readonly axis: 'x' | 'y'
  /** `1` away from the middle in the growing direction, `-1` in the other. */
  readonly dir: 1 | -1
}

function normalOf(side: Side | null): Normal | null {
  switch (side) {
    case 'top':
      return { axis: 'y', dir: -1 }
    case 'bottom':
      return { axis: 'y', dir: 1 }
    case 'left':
      return { axis: 'x', dir: -1 }
    case 'right':
      return { axis: 'x', dir: 1 }
    default:
      return null
  }
}

function stepped(point: Point, normal: Normal): Point {
  return normal.axis === 'x'
    ? { x: point.x + STUB * normal.dir, y: point.y }
    : { x: point.x, y: point.y + STUB * normal.dir }
}

/**
 * The corners between the two stub points.
 *
 * One where the ends face along different axes, two where they face along the same one. The choice
 * is always the same question: **does this route double back into the card it has just left?** Where
 * turning immediately avoids that, it turns immediately.
 */
function turns(a: Point, b: Point, out: Normal | null, back: Normal | null): Point[] {
  if (out === null && back === null) {
    // Nothing is fastened to anything, so the only thing to go on is which way is further.
    return Math.abs(b.x - a.x) >= Math.abs(b.y - a.y) ? [{ x: b.x, y: a.y }] : [{ x: a.x, y: b.y }]
  }
  if (out === null) {
    // Only the far end has an opinion: come into it along its own axis.
    return back?.axis === 'x' ? [{ x: a.x, y: b.y }] : [{ x: b.x, y: a.y }]
  }
  if (back === null || back.axis !== out.axis) {
    // Setting off along the stub is the better shape — the first two segments then run straight on —
    // but only where it does not mean going back the way the stub came.
    const along = out.axis === 'y' ? { x: a.x, y: b.y } : { x: b.x, y: a.y }
    const reverses =
      out.axis === 'y' ? Math.sign(b.y - a.y) === -out.dir : Math.sign(b.x - a.x) === -out.dir
    if (!reverses) {
      return [along]
    }
    return out.axis === 'y' ? [{ x: b.x, y: a.y }] : [{ x: a.x, y: b.y }]
  }

  /*
   * Both face along the same axis, and there are two shapes for that.
   *
   * Where a single run *along* that axis can lie beyond both stubs — two cards facing the same way,
   * or facing each other with room between them — the line goes out, across, and back in.
   *
   * Where no such run exists it has to go **round**. Two anchors facing away from each other with the
   * target behind the source is the case: any line straight across would have to pass through the
   * card it is aiming at and come at its anchor from the wrong side, which is precisely what it did.
   * So it crosses on the *other* axis instead — out, along, past the target, and in from outside.
   */
  const axis = out.axis
  const across = axis === 'y' ? 'x' : 'y'
  const beyond = Math.max(out.dir === 1 ? a[axis] : -Infinity, back.dir === 1 ? b[axis] : -Infinity)
  const before = Math.min(out.dir === -1 ? a[axis] : Infinity, back.dir === -1 ? b[axis] : Infinity)

  if (beyond <= before) {
    const mid = Math.min(Math.max((a[axis] + b[axis]) / 2, beyond), before)
    return axis === 'y'
      ? [
          { x: a.x, y: mid },
          { x: b.x, y: mid },
        ]
      : [
          { x: mid, y: a.y },
          { x: mid, y: b.y },
        ]
  }

  const round = (a[across] + b[across]) / 2
  return axis === 'y'
    ? [
        { x: round, y: a.y },
        { x: round, y: b.y },
      ]
    : [
        { x: a.x, y: round },
        { x: b.x, y: round },
      ]
}

/** Consecutive points joined at right angles, for a line whose corners somebody placed by hand. */
function chained(through: readonly Point[]): Point[] {
  const path: Point[] = []
  for (let index = 1; index < through.length; index += 1) {
    const previous = through[index - 1]
    const next = through[index]
    if (previous === undefined || next === undefined) {
      continue
    }
    if (previous.x !== next.x && previous.y !== next.y) {
      path.push(
        Math.abs(next.x - previous.x) >= Math.abs(next.y - previous.y)
          ? { x: next.x, y: previous.y }
          : { x: previous.x, y: next.y },
      )
    }
    path.push(next)
  }
  return path
}

/** The same run of points with anything that repeats a position taken out. */
function collapsed(points: readonly Point[]): Point[] {
  return points.filter(
    (point, index) =>
      index === 0 || point.x !== points[index - 1]?.x || point.y !== points[index - 1]?.y,
  )
}
/**
 * The points as an SVG path.
 *
 * Straight and right-angled lines are `M` and `L` and nothing else — they are straight between their
 * turns, and that is the whole of what they say.
 *
 * A **curve** is a run of cubics through the same points, and what decides its shape is the same
 * thing that decides an elbow's: **which way each end faces.** It leaves the right of a card heading
 * right and arrives at the top of another coming down from above, and the sweep between the two is
 * the consequence. Without that it had nothing to bend around — with no waypoints at all the control
 * points landed on the straight line between the ends, and "curved" drew a line that was simply
 * straight.
 *
 * Inside a run of waypoints the classic rule applies: each control leans a sixth of the way along the
 * line between the point *before* and the point *after*, which is what makes several segments read as
 * one curve rather than as arcs stuck end to end.
 */
export function pathOf(
  points: readonly Point[],
  routing: BoardConnection['routing'] = 'straight',
  fromSide: Side | null = null,
  toSide: Side | null = null,
): string {
  const first = points[0]
  if (first === undefined) {
    return ''
  }
  const start = `M ${round(first.x)} ${round(first.y)}`
  if (routing !== 'curved' || points.length < 2) {
    return [start, ...points.slice(1).map((one) => `L ${round(one.x)} ${round(one.y)}`)].join(' ')
  }

  const legs: string[] = []
  for (let index = 0; index < points.length - 1; index += 1) {
    const one = points[index]
    const other = points[index + 1]
    if (one === undefined || other === undefined) {
      continue
    }
    const leaving =
      index === 0
        ? leadingOut(one, other, fromSide)
        : {
            x: (other.x - (points[index - 1]?.x ?? one.x)) / 6,
            y: (other.y - (points[index - 1]?.y ?? one.y)) / 6,
          }
    const arriving =
      index === points.length - 2
        ? leadingOut(other, one, toSide)
        : {
            x: -((points[index + 2]?.x ?? other.x) - one.x) / 6,
            y: -((points[index + 2]?.y ?? other.y) - one.y) / 6,
          }
    legs.push(
      `C ${round(one.x + leaving.x)} ${round(one.y + leaving.y)}, ` +
        `${round(other.x + arriving.x)} ${round(other.y + arriving.y)}, ` +
        `${round(other.x)} ${round(other.y)}`,
    )
  }
  return [start, ...legs].join(' ')
}

/**
 * How far, and in which direction, a curve leads away from one of its ends.
 *
 * Along the anchor's own direction where there is one — that is what makes the curve leave a card
 * square to its edge instead of shearing off it. Where an end hangs free there is no direction to
 * respect, so it bows gently to one side: a curve between two bare points has nothing to curve
 * around, and drawing it dead straight would make the choice of *Curved* look like it did nothing.
 */
function leadingOut(at: Point, towards: Point, side: Side | null): Point {
  const away = Math.hypot(towards.x - at.x, towards.y - at.y)
  const lead = Math.min(Math.max(away * 0.4, LEAST_LEAD), MOST_LEAD)
  const normal = normalOf(side)
  if (normal === null) {
    // Sideways to the run, by a little. Enough to read as a curve, not enough to read as a detour.
    const along =
      away === 0 ? { x: 0, y: 0 } : { x: (towards.x - at.x) / away, y: (towards.y - at.y) / away }
    return {
      x: along.x * lead * 0.6 - along.y * lead * 0.25,
      y: along.y * lead * 0.6 + along.x * lead * 0.25,
    }
  }
  return normal.axis === 'x' ? { x: lead * normal.dir, y: 0 } : { x: 0, y: lead * normal.dir }
}

/** Short enough that two cards side by side do not bulge; long enough that a curve reads as one. */
const LEAST_LEAD = 40
const MOST_LEAD = 180

/**
 * What can be taken hold of on a selected line.
 *
 * **Two kinds, and telling them apart is the whole point.** A handle *on* an existing corner moves
 * that corner; a handle in the *middle of a leg* puts a new one in. They used to be one thing — only
 * the leg midpoints existed — which meant a corner could be added and then never moved again: taking
 * hold of it hit the midpoint beside it and laid a second corner on top of the first.
 *
 * The corner handles come last, so they are drawn over the leg handles wherever the two are close.
 */
export type Handle =
  /** An existing waypoint. Dragging it moves it — or removes it, when it is dropped back on the line. */
  | { readonly kind: 'corner'; readonly at: Point; readonly index: number }
  /** The middle of a leg. Dragging it puts a new waypoint in there. */
  | { readonly kind: 'leg'; readonly at: Point; readonly leg: number }
  /**
   * One end of the line. Dragging it takes that end somewhere else.
   *
   * A line without these is a line that can never be re-aimed: it was drawn once and stuck to
   * whatever it was dropped on for good, which is not something anybody expects of a drawing.
   */
  | { readonly kind: 'end'; readonly at: Point; readonly which: 'from' | 'to' }

export function handlesOf(corners: readonly Point[], waypoints: readonly Point[]): Handle[] {
  const legs: Handle[] = []
  for (let index = 1; index < corners.length; index += 1) {
    const previous = corners[index - 1]
    const next = corners[index]
    if (previous === undefined || next === undefined) {
      continue
    }
    legs.push({
      kind: 'leg',
      at: { x: (previous.x + next.x) / 2, y: (previous.y + next.y) / 2 },
      leg: index - 1,
    })
  }

  // A waypoint *is* a point in board coordinates, so its handle needs no looking up among the
  // corners — which is just as well, since an elbow puts corners of its own between them.
  const points: Handle[] = waypoints.map((at, index) => ({ kind: 'corner', at, index }))

  // The two ends are the first and last of the drawn path, whatever the routing put in between.
  const first = corners[0]
  const last = corners.at(-1)
  const ends: Handle[] =
    first === undefined || last === undefined
      ? []
      : [
          { kind: 'end', at: first, which: 'from' },
          { kind: 'end', at: last, which: 'to' },
        ]

  return [...legs, ...points, ...ends]
}

/**
 * The waypoints a line has after one has been dragged in at [leg].
 *
 * The legs are counted along the *drawn* path, which has more of them than there are waypoints — an
 * elbow adds a corner of its own between two of them. So the new point goes in at the position of
 * the last waypoint the leg lies beyond, which is the one thing that makes dragging a leg twice put
 * the second point after the first rather than before it.
 */
export function waypointsWith(
  waypoints: readonly Point[],
  leg: number,
  at: Point,
  routing: BoardConnection['routing'],
): Point[] {
  const perWaypoint = routing === 'elbow' ? 2 : 1
  const index = Math.min(waypoints.length, Math.floor(leg / perWaypoint))
  return [...waypoints.slice(0, index), at, ...waypoints.slice(index)]
}

/** The same waypoints with the one at [index] moved to [at]. */
export function waypointsMoved(waypoints: readonly Point[], index: number, at: Point): Point[] {
  return waypoints.map((point, where) => (where === index ? at : point))
}

export function waypointsWithout(waypoints: readonly Point[], index: number): Point[] {
  return waypoints.filter((_, where) => where !== index)
}

/**
 * What lies either side of the waypoint at [index] — its neighbours, or the ends of the line.
 *
 * This is what "back on the line" is measured against: a corner is gone when it no longer bends
 * anything, and whether it bends anything is a question about the two points it sits between.
 */
export function neighboursOf(
  waypoints: readonly Point[],
  index: number,
  from: Point,
  to: Point,
): { before: Point; after: Point } {
  return {
    before: waypoints[index - 1] ?? from,
    after: waypoints[index + 1] ?? to,
  }
}

/**
 * How far [point] lies from the straight stretch between [from] and [to].
 *
 * The distance to the *segment* and not to the infinite line through it: a corner dragged well past
 * the end of a leg is not "on" it, however neatly it lines up with it.
 */
export function distanceToSegment(point: Point, from: Point, to: Point): number {
  const dx = to.x - from.x
  const dy = to.y - from.y
  const length = dx * dx + dy * dy
  if (length === 0) {
    return Math.hypot(point.x - from.x, point.y - from.y)
  }
  const along = Math.min(
    1,
    Math.max(0, ((point.x - from.x) * dx + (point.y - from.y) * dy) / length),
  )
  return Math.hypot(point.x - (from.x + along * dx), point.y - (from.y + along * dy))
}

/**
 * The waypoints after the one at [index] has been let go of at [at] — moved, or gone.
 *
 * **Dropping a corner back onto the line removes it.** That is the gesture every diagramming tool
 * uses, and it explains itself the moment somebody tries it: a corner that bends nothing is not a
 * corner. It is deliberately not the only way — the line's own controls carry *Straighten*, which
 * takes them all out at once, so removal is reachable by keyboard and on a touch screen without
 * anybody having to know the trick.
 *
 * [within] is measured in board coordinates, so whoever calls this converts from screen pixels
 * first: at half zoom a handle is half as far from the line on the screen as it is on the board, and
 * the distance that matters is the one somebody can see.
 */
export function waypointsAfterDragging(
  waypoints: readonly Point[],
  index: number,
  at: Point,
  ends: { from: Point; to: Point },
  within: number,
): Point[] {
  const { before, after } = neighboursOf(waypoints, index, ends.from, ends.to)
  return distanceToSegment(at, before, after) <= within
    ? waypointsWithout(waypoints, index)
    : waypointsMoved(waypoints, index, at)
}

/**
 * How close to the line a corner has to be dropped to be taken out, in **screen** pixels.
 *
 * Wide enough that a finger can mean it, narrow enough that nobody loses a corner they were placing
 * deliberately near the line.
 */
export const REMOVED_WITHIN = 12

/** Two decimals is under a pixel at any zoom this surface allows, and keeps the path readable. */
function round(value: number): number {
  return Math.round(value * 100) / 100
}
